/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import {
  canFetchMoreMissions,
  canFetchMoreCourses,
  canFetchMoreProgrammes,
  canFetchMoreResources,
  buildMissionsFetchMoreVariables,
  buildCoursesFetchMoreVariables,
  buildProgrammesFetchMoreVariables,
  buildResourcesFetchMoreVariables,
  updateMissionsQuery,
  updateCoursesQuery,
  updateProgrammesQuery,
  updateResourcesQuery,
} from '../utils/pagination-helper';

interface FetchMoreHookParams {
  missionsSearch: any;
  programmesSearch: any;
  coursesSearch: any;
  resourcesSearch: any;
  skipConditions: {
    missions: boolean;
    programmes: boolean;
    courses: boolean;
    resources: boolean;
    queries: boolean;
  };
}

export const useFetchMore = ({
  missionsSearch,
  programmesSearch,
  coursesSearch,
  resourcesSearch,
  skipConditions,
}: FetchMoreHookParams) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const onFetchMore = useMemo(() => {
    return async () => {
      const canFetchMissions = canFetchMoreMissions(missionsSearch.result);
      const canFetchCourses = canFetchMoreCourses(coursesSearch.result);
      const canFetchProgrammes = canFetchMoreProgrammes(programmesSearch.result);
      const canFetchResources = canFetchMoreResources(resourcesSearch.result);

      if (!canFetchMissions && !canFetchProgrammes && !canFetchResources && !canFetchCourses) {
        return;
      }

      setIsLoadingMore(true);

      try {
        const promises: Promise<any>[] = [];

        if (!skipConditions.missions && !skipConditions.queries && missionsSearch.result && canFetchMissions) {
          promises.push(
            missionsSearch.fetchMore({
              variables: buildMissionsFetchMoreVariables(missionsSearch.queryVariables, missionsSearch.result),
              updateQuery: updateMissionsQuery,
            }),
          );
        }

        if (!skipConditions.courses && !skipConditions.queries && coursesSearch.result && canFetchCourses) {
          promises.push(
            coursesSearch.fetchMore({
              variables: buildCoursesFetchMoreVariables(coursesSearch.queryVariables, coursesSearch.result),
              updateQuery: updateCoursesQuery,
            }),
          );
        }

        if (!skipConditions.programmes && !skipConditions.queries && programmesSearch.result && canFetchProgrammes) {
          promises.push(
            programmesSearch.fetchMore({
              variables: buildProgrammesFetchMoreVariables(programmesSearch.queryVariables, programmesSearch.result),
              updateQuery: updateProgrammesQuery,
            }),
          );
        }

        if (!skipConditions.resources && !skipConditions.queries && resourcesSearch.result && canFetchResources) {
          promises.push(
            resourcesSearch.fetchMore({
              variables: buildResourcesFetchMoreVariables(resourcesSearch.queryVariables, resourcesSearch.result),
              updateQuery: updateResourcesQuery,
            }),
          );
        }

        await Promise.all(promises);
      } finally {
        setIsLoadingMore(false);
      }
    };
  }, [missionsSearch, programmesSearch, coursesSearch, resourcesSearch, skipConditions]);

  return {
    onFetchMore,
    isLoadingMore,
  };
};
